import React from 'react'
import { Head } from '@inertiajs/react'
import { Edit3, Trash2, Copy, Check, X } from 'lucide-react'
import { AdminLayout } from '@/components/admin/templates/AdminLayout'
import { Surface } from '@/components/admin/atoms/layout/Surface'
import { Stack } from '@/components/admin/atoms/layout/Stack'
import { Inline } from '@/components/admin/atoms/layout/Inline'
import { Padding } from '@/components/admin/atoms/layout/Padding'
import { FlexMax } from '@/components/admin/atoms/layout/FlexMax'
import { MediaPanelLayout } from '@/components/admin/atoms/layout/MediaPanelLayout'
import { MediaGridPanel } from '@/components/admin/atoms/layout/MediaGridPanel'
import { MediaDetailPanel } from '@/components/admin/atoms/layout/MediaDetailPanel'
import { MediaGrid } from '@/components/admin/atoms/layout/MediaGrid'
import { Heading } from '@/components/admin/atoms/typography/Heading'
import { Text } from '@/components/admin/atoms/typography/Text'
import { Muted } from '@/components/admin/atoms/typography/Muted'
import { EmptyState } from '@/components/admin/atoms/feedback/EmptyState'
import { ActionButton } from '@/components/admin/atoms/actions/ActionButton'
import { IconButton } from '@/components/admin/atoms/actions/IconButton'
import { FilterToggle } from '@/components/admin/atoms/actions/FilterToggle'
import { UploadButton } from '@/components/admin/atoms/actions/UploadButton'
import { TextInput } from '@/components/admin/atoms/inputs/TextInput'
import { MediaThumbnail } from '@/components/admin/atoms/media/MediaThumbnail'
import { MediaPreviewImage } from '@/components/admin/atoms/media/MediaPreviewImage'
import { SearchInput } from '@/components/admin/molecules/data/SearchInput'
import { ConfirmDialog } from '@/components/admin/organisms/ConfirmDialog'
import { Divider } from '@/components/admin/atoms/layout/Divider'
import { formatBytes, formatDate } from '@/lib/utils'
import { useMediaIndexVM } from './Index.vm'
import type { PageProps } from '@/types/inertia.d'
import type { MediaItem } from '@/types/admin'

type Props = PageProps<{ media: MediaItem[] }>

const MediaIndex: React.FC<Props> = ({ media: initialMedia }) => {
  const vm = useMediaIndexVM(initialMedia)

  return (
    <AdminLayout breadcrumbs={[{ label: 'Media' }]}>
      <Head title="Media" />
      <Stack gap="6">
        <Inline justify="between" align="center">
          <Heading level={2}>Media Library</Heading>
          <UploadButton onUpload={vm.handleUpload} loading={vm.uploading}>
            Upload Media
          </UploadButton>
        </Inline>

        <MediaPanelLayout>
          <MediaGridPanel>
            <Surface variant="card" radius="lg">
              <Padding all="5">
                <Stack gap="4">
                  <Inline gap="3" align="center">
                    <FlexMax maxW="sm">
                      <SearchInput value={vm.search} onChange={vm.setSearch} placeholder="Search files…" />
                    </FlexMax>
                    <Inline gap="1">
                      {vm.filterTypes.map((f) => (
                        <FilterToggle
                          key={f}
                          label={f.charAt(0).toUpperCase() + f.slice(1)}
                          active={vm.typeFilter === f}
                          onClick={() => vm.setTypeFilter(f)}
                        />
                      ))}
                    </Inline>
                    <Muted>{vm.filtered.length} files</Muted>
                  </Inline>

                  <Divider />

                  {vm.media.length === 0 && !vm.uploading && (
                    <EmptyState
                      title="No media files"
                      description="Upload images or documents to get started."
                      action={
                        <UploadButton onUpload={vm.handleUpload}>
                          Upload Files
                        </UploadButton>
                      }
                    />
                  )}

                  {vm.filtered.length > 0 && (
                    <MediaGrid>
                      {vm.filtered.map((item) => (
                        <MediaThumbnail
                          key={item.id}
                          url={item.url}
                          name={item.name}
                          filename={item.filename}
                          isImage={item.is_image}
                          selected={vm.selected?.id === item.id}
                          onClick={() => vm.setSelected(item)}
                        />
                      ))}
                    </MediaGrid>
                  )}
                </Stack>
              </Padding>
            </Surface>
          </MediaGridPanel>

          {vm.selected && (
            <MediaDetailPanel>
              <Surface variant="card" radius="lg">
                <Padding all="5">
                  <Stack gap="4">
                    <Inline justify="between" align="start">
                      <Text size="sm" weight="semibold">File Details</Text>
                      <IconButton
                        icon={<X style={{ width: 16, height: 16 }} />}
                        label="Close"
                        onClick={vm.closeDetail}
                      />
                    </Inline>
                    <Divider />

                    <MediaPreviewImage
                      url={vm.selected.url}
                      name={vm.selected.name}
                      isImage={vm.selected.is_image}
                    />

                    <Stack gap="2">
                      {vm.editingName ? (
                        <Stack gap="2">
                          <TextInput
                            value={vm.editName}
                            onChange={vm.setEditName}
                          />
                          <Inline gap="2">
                            <ActionButton variant="primary" size="xs" onClick={vm.handleSaveName} loading={vm.savingName}>Save</ActionButton>
                            <ActionButton variant="ghost" size="xs" onClick={vm.cancelEditName}>Cancel</ActionButton>
                          </Inline>
                        </Stack>
                      ) : (
                        <Inline justify="between" align="center">
                          <Text size="sm" weight="medium" as="span">{vm.selected.name}</Text>
                          <IconButton
                            icon={<Edit3 style={{ width: 14, height: 14 }} />}
                            label="Edit name"
                            size="xs"
                            onClick={vm.startEditName}
                          />
                        </Inline>
                      )}

                      <Divider />

                      <Stack gap="1">
                        <Inline justify="between">
                          <Muted>Type</Muted>
                          <Text size="xs" as="span">{vm.selected.content_type}</Text>
                        </Inline>
                        <Inline justify="between">
                          <Muted>Size</Muted>
                          <Text size="xs" as="span">{formatBytes(vm.selected.byte_size)}</Text>
                        </Inline>
                        <Inline justify="between">
                          <Muted>Uploaded</Muted>
                          <Text size="xs" as="span">{formatDate(vm.selected.created_at)}</Text>
                        </Inline>
                        <Inline justify="between">
                          <Muted>Used in</Muted>
                          <Text size="xs" as="span">{vm.selected.usage_count} post{vm.selected.usage_count !== 1 ? 's' : ''}</Text>
                        </Inline>
                      </Stack>

                      <Divider />

                      <Stack gap="2">
                        <ActionButton variant="outline" size="sm" onClick={vm.handleCopyUrl}>
                          {vm.copied ? <Check style={{ width: 16, height: 16 }} /> : <Copy style={{ width: 16, height: 16 }} />}
                          {vm.copied ? 'Copied!' : 'Copy URL'}
                        </ActionButton>
                        <ActionButton variant="error" size="sm" onClick={() => vm.setDeleteId(vm.selected!.id)}>
                          <Trash2 style={{ width: 16, height: 16 }} />
                          Delete
                        </ActionButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </Padding>
              </Surface>
            </MediaDetailPanel>
          )}
        </MediaPanelLayout>
      </Stack>

      <ConfirmDialog
        open={vm.deleteDialogOpen}
        title="Delete Media"
        description="This file will be permanently deleted. Posts using it may break. This action cannot be undone."
        onConfirm={vm.handleDelete}
        onCancel={() => vm.setDeleteId(null)}
        loading={vm.deleting}
      />
    </AdminLayout>
  )
}

export default MediaIndex
